import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface BlockchainExplainerDiagramProps {
	className?: string;
}

/**
 * Static SVG diagram explaining the event → batch → merkle root → on-chain
 * anchor flow. No data props — purely illustrative.
 */
function BlockchainExplainerDiagram({
	className,
}: BlockchainExplainerDiagramProps): ReactNode {
	return (
		<div
			className={cn("overflow-x-auto", className)}
			aria-label="Blockchain flow diagram"
		>
			<svg
				width={600}
				height={200}
				viewBox="0 0 600 200"
				role="img"
				aria-label="Flow: Event to Batch to Merkle Root to On-chain Anchor"
				className="mx-auto"
			>
				{/* Step boxes */}
				{/* Event */}
				<rect
					x={20}
					y={70}
					width={100}
					height={60}
					rx={8}
					className="fill-primary/10 stroke-primary/40"
					strokeWidth={1.5}
				/>
				<text
					x={70}
					y={95}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					Event
				</text>
				<text
					x={70}
					y={112}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Hash recorded
				</text>

				{/* Arrow 1 */}
				<line
					x1={120}
					y1={100}
					x2={155}
					y2={100}
					stroke="currentColor"
					strokeWidth={1.5}
					className="text-border"
				/>
				<polygon points="155,95 165,100 155,105" className="fill-border" />

				{/* Batch */}
				<rect
					x={165}
					y={70}
					width={100}
					height={60}
					rx={8}
					className="fill-primary/10 stroke-primary/40"
					strokeWidth={1.5}
				/>
				<text
					x={215}
					y={95}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					Batch
				</text>
				<text
					x={215}
					y={112}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Groups events
				</text>

				{/* Arrow 2 */}
				<line
					x1={265}
					y1={100}
					x2={300}
					y2={100}
					stroke="currentColor"
					strokeWidth={1.5}
					className="text-border"
				/>
				<polygon points="300,95 310,100 300,105" className="fill-border" />

				{/* Merkle Root */}
				<rect
					x={310}
					y={70}
					width={110}
					height={60}
					rx={8}
					className="fill-primary/10 stroke-primary/40"
					strokeWidth={1.5}
				/>
				<text
					x={365}
					y={93}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					Merkle
				</text>
				<text
					x={365}
					y={107}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					Root
				</text>
				<text
					x={365}
					y={122}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Fingerprint
				</text>

				{/* Arrow 3 */}
				<line
					x1={420}
					y1={100}
					x2={455}
					y2={100}
					stroke="currentColor"
					strokeWidth={1.5}
					className="text-border"
				/>
				<polygon points="455,95 465,100 455,105" className="fill-border" />

				{/* On-chain Anchor */}
				<rect
					x={465}
					y={60}
					width={115}
					height={80}
					rx={8}
					className="fill-statusSuccess/10 stroke-statusSuccess/40"
					strokeWidth={1.5}
				/>
				<text
					x={522}
					y={92}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					On-chain
				</text>
				<text
					x={522}
					y={106}
					textAnchor="middle"
					className="fill-foreground text-xs font-semibold"
					fontSize={11}
					fontWeight={600}
				>
					Anchor
				</text>
				<text
					x={522}
					y={123}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Immutable proof
				</text>

				{/* Step labels */}
				<text
					x={70}
					y={155}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Step 1
				</text>
				<text
					x={215}
					y={155}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Step 2
				</text>
				<text
					x={365}
					y={155}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Step 3
				</text>
				<text
					x={522}
					y={155}
					textAnchor="middle"
					className="fill-mutedForeground"
					fontSize={9}
				>
					Step 4
				</text>
			</svg>
		</div>
	);
}
BlockchainExplainerDiagram.displayName = "BlockchainExplainerDiagram";

export default BlockchainExplainerDiagram;
export { BlockchainExplainerDiagram };
